import { createContext, useContext } from 'react';
import type { XAiChatbotProps, XAiConversationsProps, Messages } from '@/types';
// 定义 context 的类型
// 解决 onScroll 冲突，显式声明 onScroll 类型
export interface XAiContextType extends Omit<XAiChatbotProps, 'onScroll' | 'messages'>, Omit<XAiConversationsProps, 'onScroll'> {
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  messages: Messages[];
  providerId?: string;
  token: string;
  error: string | null;
  // 加载更多 session
  loadMoreSessions?: () => void;
  // 标识是否在真实的 Provider 中
  isInProvider?: boolean;
}

// 默认 context 值
const defaultContextValue: XAiContextType = {
  providerId: undefined,
  token: '',
  messages: [],
  loading: false,
  error: null,
  emptyStateImage: '',
  emptyStateText: '',

  isInProvider: false,
};

// 安全地创建 context，避免 React 为 null 的情况
const XAiContext: React.Context<XAiContextType> = (() => {
  try {
    return createContext<XAiContextType>(defaultContextValue);
  } catch {
    // 如果 React 不可用，创建一个 fallback
    // eslint-disable-next-line
    const fallback: React.Context<XAiContextType> = {} as React.Context<XAiContextType>;
    return fallback;
  }
})();

export { XAiContext };

// 改进的 useXAi hook
export const useXAi = () => {
  try {
    const context = useContext(XAiContext);

    // 如果没有在 Provider 中，返回 undefined
    if (!context?.isInProvider) {
      return undefined;
    }

    return context;
  } catch {
    // 如果 useContext 失败，返回 undefined
    return undefined;
  }
};

// 获取所有可用的 Provider ID
export const useXAiProviders = () => {
  try {
    const context = useContext(XAiContext);

    // 如果没有在 Provider 中，返回空数组
    if (!context?.isInProvider) {
      return [];
    }

    // 返回当前 Provider 的 ID（如果有的话）
    return context.providerId ? [context.providerId] : [];
  } catch {
    return [];
  }
};
